import { Notifier } from "@airbrake/browser";
import { IErrorNotifier, TSeverity } from "./types";

function airbrakeConfigs() {
  return {
    projectId: import.meta.env.VITE_AIRBRAKE_PROJECT_ID,
    projectKey: import.meta.env.VITE_AIRBRAKE_PROJECT_KEY,
    environment: import.meta.env.VITE_AIRBRAKE_ENVIRONMENT,
    ignoreWindowError:
      import.meta.env.VITE_AIRBRAKE_IGNORE_WINDOW_ERROR === "1",
    blackWordReg: import.meta.env.VITE_AIRBRAKE_BLACK_WORD_REG,
  };
}

function toErrorNotify(
  airbrakeNotifier: Pick<Notifier, "notify">,
  appName: string
): IErrorNotifier {
  const notify: IErrorNotifier["notify"] = async (params) => {
    if (params instanceof Error || typeof params === "string") {
      await airbrakeNotifier.notify(params);
      return;
    }

    await airbrakeNotifier.notify({
      error: params.error ?? params.msg,
      params: {
        msg: params.msg,
        context: params.context,
      },
      context: {
        severity: params.severity
          ? severityMap.get(params.severity)
          : AIRBRAKE_SERVERITY_ERROR,
        component: appName,
      },
    });
  };

  return {
    notify,
  };
}

function createAirbrakeNotifier(appName: string): IErrorNotifier | null {
  const config = airbrakeConfigs();

  const {
    projectId,
    projectKey,
    environment,
    ignoreWindowError,
    blackWordReg,
  } = config;

  if (projectId == null || projectKey == null) {
    return null;
  }

  const airbrake = new Notifier({
    projectId,
    projectKey,
    environment,
    instrumentation: {
      onerror: !ignoreWindowError,
    },
  });

  if (blackWordReg?.trim()) {
    const blackWordRegExp = new RegExp(blackWordReg);
    airbrake.addFilter((notice) => {
      notice.errors = notice.errors?.filter(
        ({ message }) => !message.match(blackWordRegExp)
      );
      if (Number(notice.errors?.length) > 0) return notice;
      return null;
    });
  }

  return toErrorNotify(airbrake, appName);
}

// https://docs.airbrake.io/docs/faq/what-is-severity/
const AIRBRAKE_SERVERITY_WARN = "warning";
const AIRBRAKE_SERVERITY_ERROR = "error";
const AIRBRAKE_SERVERITY_CRIT = "critical";

const severityMap = new Map<TSeverity, string>([
  ["warn", AIRBRAKE_SERVERITY_WARN],
  ["error", AIRBRAKE_SERVERITY_ERROR],
  ["crit", AIRBRAKE_SERVERITY_CRIT],
]);

export const _test = { toErrorNotify };
export { createAirbrakeNotifier };
